import { Injectable, inject } from '@angular/core';
import { RootReducer, Store } from '@app/app.reducers';
import {
	CartItem,
	updatePrintfile,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import {
	getMyProducts,
	updateListProduct,
} from '@app/checkout/modules/checkout-shared/reducers/persisted-list.reducer';
import { AntivirusStatus, PreflightStatus } from '@app/file-api/file-api.constants';
import {
	FileApiService,
	FilePropsFragment,
	FileType,
	UploadFileDataInput,
	onPreflightProgressSubscription_onPreflightProgress_ProgressUpdate as preflightProgressUpdate,
	retryPreflightMutation,
	uploadFileMutation_uploadFile_File,
} from '@app/file-api/file-api.service';
import { updateFile, updatePreflightProgress } from '@app/shared/reducers/file.reducer';
import { FileUploadStatus, finishUpload, startUpload } from '@app/shared/reducers/upload.reducer';
import { NewRelic } from '@app/shared/services/newrelic.service';
import { getDebug } from '@app/shared/utils/util';
import { ApiState, Kinds } from '@granodigital/grano-remote-data';
import type { MutationResult } from 'apollo-angular';
import { Observable, Subject, Subscription, concat, firstValueFrom, from } from 'rxjs';
import { catchError, filter, map, share, shareReplay, takeUntil, withLatestFrom } from 'rxjs/operators';

/**
 * Extract the upload data type for a given FileType
 */
export type FileUploadFileInput<
	T extends FileType,
	TData extends UploadFileDataInput = UploadFileDataInput,
	K extends Lowercase<T> = Lowercase<T>,
> = K extends keyof TData ? (TData[K] extends never ? never : NonNullable<TData[K]>) : never;

export type FileUploadDataInput<
	T,
	E extends FileType,
	L extends Lowercase<E> = Lowercase<E>,
> = L extends keyof T ? PickRequired<T, L> : T;

type ValidatedUploadFileResponse = uploadFileMutation_uploadFile_File & {
	presignedPost: Exclude<uploadFileMutation_uploadFile_File, null>;
};

export const growingNumbersOnly =
	<T extends Record<string, any> | undefined>(key: keyof Exclude<T, undefined>) =>
	(source: Observable<T>): Observable<T> => {
		let previousValue: number | undefined;
		return source.pipe(
			filter((value) => {
				const num = typeof value === 'object' && key in value ? ((value as any)[key] as number) : 0;
				if (previousValue === undefined || num > previousValue) {
					previousValue = num;
					return true;
				}
				return false;
			}),
		);
	};

/** A service for uploading files and handling antivirus + pdf preflight */
@Injectable({ providedIn: 'root' })
export class FileService {
	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly newRelic = inject(NewRelic);
	private readonly fileApi = inject(FileApiService);

	private readonly debug = getDebug('FileService');
	private readonly onFileUpdateSubscriptions = new Map<string, Subscription>();
	private readonly onPreflightProgressSubscriptions = new Map<string, Subscription>();

	/**
	 * Perform the full file upload pipeline.
	 * Upload status is updated to the redux store.
	 * @param file The file to upload.
	 * @param type The file type. (e.g. FileType.Asset)
	 * @param options The upload options.
	 * @returns Data available after the upload preparation is done is returned when the returned promise is resolved.
	 * 					The uploadDone promise resolves when the actual file upload is done.
	 */
	async uploadFile<T extends FileType, TInput extends FileUploadFileInput<T>>(
		file: File,
		type: T,
		options: TInput,
	): Promise<{
		/* Resolves when the file upload is done, resolves on success, rejects if not */
		uploadDone: Promise<Response | Error>;
		data: { key: string; ttl: number };
	}> {
		const response = await firstValueFrom(
			this.prepareUpload(file.name, type, options).pipe(
				map((response) => {
					// Basic validation
					if (!response) {
						const error = new Error(`File upload failed`);
						this.newRelic.noticeError(error, { file: file.name, options });
						throw error;
					}
					if (!response.presignedPost) {
						const error = new Error(`presignedPost is missing from the printfile upload response!`);
						this.newRelic.noticeError(error, { file: file.name, options });
						throw error;
					}
					return response as ValidatedUploadFileResponse;
				}),
				catchError((error: Error) => {
					this.debug('File upload error', error);
					this.newRelic.noticeError(error);
					throw error;
				}),
			),
		);

		const { key, ttl, presignedPost } = response;
		const { url, fields } = presignedPost;
		this.store.dispatch(startUpload(key));

		const body = new FormData();
		for (const [key, value] of fields) {
			body.append(key, value);
		}
		body.append('file', file, key);

		this.debug('Uploading file', key);
		const uploadDone = fetch(url, { body, method: 'POST', mode: 'cors' })
			.then(async (res) => {
				if (res.ok) {
					this.debug('File uploaded!', { key, res, body: await res.text() });
					this.store.dispatch(finishUpload(key, FileUploadStatus.Success));
				} else {
					this.handleUploadError(key)(new Error(await res.text()));
				}
				return res;
			})
			.catch((err: Error) => {
				this.handleUploadError(key)(err);
				throw err;
			});

		// TODO: Refactor to use remote data.
		return { uploadDone, data: { key, ttl } };
	}

	private readonly fileMetadataObservables = new Map<
		string,
		Observable<FilePropsFragment | undefined>
	>();
	/** Gets file metadata from the file service and subscribes to updates. */
	getFileMetadata$(key: string): Observable<FilePropsFragment | undefined> {
		let getFile$ = this.fileMetadataObservables.get(key);
		if (getFile$) return getFile$;
		getFile$ = concat(
			from(this.fileApi.getFile({ key })).pipe(
				this.debug.observe('getFile$'),
				this.catchGetFileError(key, 'getFile$'),
				map((val) => val.data?.getFile ?? undefined),
			),

			// Alternative to subscription would using watchQuery. This would poll for changes and update the cache on change.
			this.fileApi.onFileUpdate({ key }).pipe(
				this.debug.observe('onFileUpdate$'),
				this.catchGetFileError(key, 'onFileUpdate$'),
				map((val) => val.data?.onFileUpdate ?? undefined),
			),
		).pipe(
			this.debug.observe('watchFileMetadata$'),
			catchError((error) => {
				this.fileMetadataObservables.delete(key);
				throw error;
			}),
			share(),
		);

		this.fileMetadataObservables.set(key, getFile$);
		return getFile$;
	}

	/**
	 * Starts watch for file status updates for the specified printfile.
	 * Stores ongoing subscriptions internally in this.onFileUpdateSubscriptions, and this.onPreflightProgressSubscriptions.
	 * @param printfileKey The printfile key.
	 */
	watchFileStatusUpdates(printfileKey: string): void {
		if (!this.onFileUpdateSubscriptions.get(printfileKey)) {
			const destroyOnFileUpdate = new Subject<boolean>();
			const fileSubscription = this.getFileMetadata$(printfileKey)
				.pipe(
					takeUntil(destroyOnFileUpdate),
					this.debug.observe('getFileMetadata$'),
					// take until the status checks are done, and then remove from fileUpdateSubscriptions.
					filter(Boolean), // skip if there's no printfile, e.g. in case of editor files,
					withLatestFrom(this.store.select(getMyProducts)),
				)
				.subscribe({
					next: ([file, myProductsResponse]: [
						FilePropsFragment,
						ApiState<api.ProductListWithProductsDto>,
					]) => {
						this.debug('printfile update', file);

						const status =
							file.antivirusState?.status === AntivirusStatus.PENDING ||
							file.preflightState?.report?.status === PreflightStatus.PENDING
								? 'unprocessed'
								: 'processed';

						if (status === 'processed') {
							destroyOnFileUpdate.next(true);
							destroyOnFileUpdate.complete();
						}

						this.store.dispatch(updateFile(printfileKey, file));

						// TODO (ED-2480): Trigger these updates elsewhere by subscribing to file updates via store.select(getFile).
						const printfile: Partial<api.PrintfileDto> = {
							key: printfileKey,
							status,
							report: file.preflightState?.report as api.PrintfileReportDto,
						};

						this.store.dispatch(updatePrintfile(printfile));

						if (myProductsResponse.kind === Kinds.Success) {
							const myProducts = myProductsResponse.data;
							const productInMyProducts = myProducts?.products?.find(
								(item) => item.options.printfile?.key === printfileKey,
							);
							if (productInMyProducts) {
								const updatedProduct: CartItem = {
									...productInMyProducts,
									isProcessing: status === 'unprocessed',
									options: {
										...productInMyProducts.options,
										printfile: { ...productInMyProducts.options.printfile!, ...printfile },
									},
								};
								this.store.dispatch(updateListProduct('my-products', updatedProduct));
							}
						}
					},
					error: (error) => {
						this.onFileUpdateSubscriptions.delete(printfileKey);
						this.newRelic.noticeError(error, { printfileKey });
					},
				});
			this.onFileUpdateSubscriptions.set(printfileKey, fileSubscription);
		}

		if (!this.onPreflightProgressSubscriptions.get(printfileKey)) {
			const destroyOnPreflightProgress = new Subject<boolean>();
			const preflightSubscription = this.onPreflightProgress$(printfileKey)
				.pipe(
					takeUntil(destroyOnPreflightProgress),
					growingNumbersOnly('progress'),
					this.debug.observe('onPreflightProgress$'),
					filter(Boolean), // skip if there's no printfile, e.g. in case of editor files,
				)
				.subscribe({
					next: ({ key, progress }) => {
						this.debug('preflight progress', { key, progress });
						this.store.dispatch(updatePreflightProgress(key, progress));
						if (progress === 100) {
							destroyOnPreflightProgress.next(true);
							destroyOnPreflightProgress.complete();
						}
					},
					error: (error) => {
						this.onPreflightProgressSubscriptions.delete(printfileKey);
						this.newRelic.noticeError(error, { printfileKey: printfileKey });
					},
				});
			this.onPreflightProgressSubscriptions.set(printfileKey, preflightSubscription);
		}
	}

	/**
	 * Subscribe to preflight progress updates.
	 * @param key The file key.
	 * @returns The file metadata.
	 */
	onPreflightProgress$(key: string): Observable<preflightProgressUpdate | undefined> {
		return this.fileApi.onPreflightProgress({ key }).pipe(
			this.debug.observe('onPreflightProgress$'),
			catchError((error, caught) => {
				this.debug('onPreflightProgress$ error', error, caught);
				throw error;
			}),
			map((val) => val.data?.onPreflightProgress ?? undefined),
			shareReplay(),
		);
	}

	/**
	 * Create file metadata entry and get the presigned post for uploading.
	 * @param data If `type` is statically inferrable, the accurate properties are required.
	 */
	private prepareUpload<T extends FileType, TInput extends FileUploadFileInput<T>>(
		filename: string,
		type: T,
		input: TInput,
	): Observable<uploadFileMutation_uploadFile_File | null> {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const data = { [type.toLowerCase()]: input } as FileUploadDataInput<UploadFileDataInput, T>;
		return this.fileApi
			.uploadFile({
				filename,
				input: { type, data },
			})
			.pipe(
				map((val) => {
					return val.data?.uploadFile ?? null;
				}),
			);
	}

	private readonly catchGetFileError = <T>(key: string, operation: string) =>
		catchError((error: Error, caught: Observable<T>) => {
			this.debug(`${operation} error`, error, caught);
			this.newRelic.noticeError(error);
			throw error;
		});

	/** Handle errors. */
	private readonly handleUploadError = (key: string) => (err: unknown) => {
		this.store.dispatch(finishUpload(key, FileUploadStatus.Error));
		this.newRelic.noticeError(err);
	};

	/**
	 * Retry preflight processing for a file.
	 * @param key - The file key to retry processing for.
	 * @returns Observable that emits when the retry operation completes.
	 */
	retryPreflight(key: string): Observable<MutationResult<retryPreflightMutation>> {
		this.debug('Retrying preflight for file', key);
		return this.fileApi.retryPreflight({ key }).pipe(
			map((result) => {
				this.debug('Retry preflight successful for file', key, result);
				return result;
			}),
			catchError((error: unknown) => {
				this.debug('Retry preflight failed for file', key, error);
				this.newRelic.noticeError(error, { fileKey: key });
				throw error;
			}),
		);
	}
}
